import React, { useState } from 'react'
import styled from '@emotion/styled'
import * as yup from 'yup'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'
import { InputAdornment, IconButton } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useMsalAuthentication } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'

import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'

import microsoftLogo from '../../images/microsoftLogo.svg'

import { account } from '../../images/images.js'
import { postLogin } from '../../dataClient/apiCalls'
import { useAuth } from '../../providers/AuthProvider'
import { CenteredForm, FullWidthButtonContainer, FlexColumn, FlexRow } from '../styles/Containers'
import { Header, SmallBodyText } from '../styles/Text.js'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton, TextButton } from '../common/Button'
import FormInput from '../inputFields/FormInput'
import { getRuntimeEnv } from '../../runtimeEnv'

const Image = styled.img`
  width: 250px;
`

const LinkContainer = styled.div`
  align-self: flex-end;
`

const MicrosoftLogo = styled.img`
  width: 15px;
`

const loginSchema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required')
})

function LoginForm({ onSuccess = () => {} }) {
  const request = {
    scopes: [`api://${getRuntimeEnv('REACT_APP_AZURE_CLIENT_ID')}/access_as_user`]
  }
  const { login } = useMsalAuthentication(InteractionType.Silent, request)

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [loadingMicrosoft, setLoadingMicrosoft] = useState(false)
  const [errorMicrosoft, setErrorMicrosoft] = useState(false)

  const { setAuthState } = useAuth()
  const navigate = useNavigate()

  const methods = useForm({
    resolver: yupResolver(loginSchema)
  })
  const { handleSubmit } = methods

  async function onSubmit(data) {
    setLoading(true)
    setError(false)

    postLogin(data.email, data.password)
      .then(response => {
        localStorage.setItem('loginMethod', 'manual')
        localStorage.setItem('accessToken', response.data.accessToken)
        setAuthState({ accessToken: response.data.accessToken, loginMethod: 'manual', isLoggedIn: true })
        onSuccess()
      })
      .catch(e => {
        setError(true)
        console.error('error logging in', e)

        localStorage.removeItem('loginMethod')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  async function loginWithMicrosoft() {
    try {
      setLoadingMicrosoft(true)
      setErrorMicrosoft(false)

      console.info('microsoft login attempt')
      const loginResponse = await login(InteractionType.Popup, request)
      const account = loginResponse.account

      if (!account) throw new Error('No account found after login')

      console.info('microsoft login succeeded', account.username)

      localStorage.setItem('loginMethod', 'microsoft')
      setAuthState({ loginMethod: 'microsoft', isLoggedIn: true })

      setLoadingMicrosoft(false)

      onSuccess()
    } catch (e) {
      if (e.errorCode !== 'user_cancelled') {
        console.error('error logging in with Microsoft', e)
        setErrorMicrosoft(true)
      }
      localStorage.removeItem('loginMethod')
      setLoadingMicrosoft(false)
    }
  }

  const handleClickShowPassword = () => setShowPassword(show => !show)
  const handleMouseDownPassword = event => event.preventDefault()

  return (
    <FormProvider {...methods}>
      <CenteredForm onSubmit={handleSubmit(onSubmit)}>
        <Image src={account} />
        <Header>Log in to VivaCitadel</Header>
        <FormInput required={true} id="email" label="Email" basis="100%" />
        <FlexColumn gap="10px" width="100%">
          <FormInput
            required={true}
            id="password"
            label="Password"
            basis="100%"
            props={{
              type: showPassword ? 'text' : 'password'
            }}
            inputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'hide password' : 'show password'}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <LinkContainer>
            <TextButton onClick={() => navigate('/resetpassword')}>Forgot your password?</TextButton>
          </LinkContainer>
        </FlexColumn>
        <FullWidthButtonContainer>
          <Button type="submit" loading={loading} disabled={loading || loadingMicrosoft}>
            Log in
          </Button>
          {error && <ErrorMessage>Invalid email or password. Please try again.</ErrorMessage>}
        </FullWidthButtonContainer>
        <SmallBodyText>or</SmallBodyText>
        <FullWidthButtonContainer>
          <SecondaryButton onClick={loginWithMicrosoft} loading={loadingMicrosoft} disabled={loadingMicrosoft || loading} type="button">
            <FlexRow gap="10px">
              <MicrosoftLogo src={microsoftLogo} />
              Log in with Microsoft
            </FlexRow>
          </SecondaryButton>
          {errorMicrosoft && <ErrorMessage>There was an error authenticating. Please try again.</ErrorMessage>}
        </FullWidthButtonContainer>
      </CenteredForm>
    </FormProvider>
  )
}

LoginForm.propTypes = {
  onSuccess: PropTypes.func
}

export default LoginForm
